import React, { useState, useEffect, useCallback } from 'react';
import { fetchClasses, fetchStandardsCoverage } from '../../api/classes';
import styles from './StandardsCoveragePage.module.css';

function CoverageBar({ covered, inUnitsOnly, total }) {
  if (total === 0) return null;
  const coveredPct = (covered / total) * 100;
  const unitsPct = (inUnitsOnly / total) * 100;

  return (
    <div className={styles.bar}>
      <div className={styles.barCovered} style={{ width: `${coveredPct}%` }} />
      <div className={styles.barUnits} style={{ width: `${unitsPct}%` }} />
    </div>
  );
}

function StandardRow({ standard, status }) {
  const icon = status === 'covered' ? '\u2705' : status === 'inUnitsOnly' ? '\u23F3' : '\u274C';
  const statusClass = status === 'covered' ? styles.statusCovered
    : status === 'inUnitsOnly' ? styles.statusUnits
    : styles.statusUncovered;

  return (
    <div className={`${styles.standardRow} ${statusClass}`}>
      <span className={styles.statusIcon}>{icon}</span>
      <span className={styles.standardCode}>{standard.standardCode}</span>
      <span className={styles.standardDesc}>{standard.description}</span>
    </div>
  );
}

function StandardsCoveragePage() {
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const result = await fetchClasses();
        if (result.success && result.data.length > 0) {
          setClasses(result.data);
          setSelectedClassId(result.data[0].id);
        }
      } catch (err) {
        console.error('Failed to load classes:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const loadCoverage = useCallback(async () => {
    if (!selectedClassId) return;
    try {
      setLoading(true);
      const result = await fetchStandardsCoverage(selectedClassId);
      if (result.success) {
        setCoverage(result.data);
      }
    } catch (err) {
      console.error('Failed to load coverage:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedClassId]);

  useEffect(() => {
    loadCoverage();
  }, [loadCoverage]);

  // Group standards by domain for display
  const groupByDomain = (standards) => {
    const groups = {};
    for (const s of standards) {
      if (!groups[s.domainCode]) {
        groups[s.domainCode] = { domainName: s.domainName, standards: [] };
      }
      groups[s.domainCode].standards.push(s);
    }
    return groups;
  };

  const getFilteredStandards = () => {
    if (!coverage) return [];
    const all = [];
    for (const s of coverage.standards.covered) all.push({ ...s, status: 'covered' });
    for (const s of coverage.standards.inUnitsOnly) all.push({ ...s, status: 'inUnitsOnly' });
    for (const s of coverage.standards.uncovered) all.push({ ...s, status: 'uncovered' });

    if (filter === 'covered') return all.filter(s => s.status === 'covered');
    if (filter === 'inUnitsOnly') return all.filter(s => s.status === 'inUnitsOnly');
    if (filter === 'uncovered') return all.filter(s => s.status === 'uncovered');
    return all;
  };

  if (loading && !coverage) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>Loading...</div>
      </div>
    );
  }

  if (classes.length === 0) {
    return (
      <div className={styles.page}>
        <div className={styles.empty}>
          <h2>No classes yet</h2>
          <p>Create a class through the onboarding flow to see standards coverage.</p>
        </div>
      </div>
    );
  }

  const filtered = getFilteredStandards();
  const grouped = groupByDomain(filtered);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Standards Coverage</h1>
        <select
          className={styles.classSelect}
          value={selectedClassId || ''}
          onChange={(e) => setSelectedClassId(Number(e.target.value))}
        >
          {classes.map((cls) => (
            <option key={cls.id} value={cls.id}>{cls.name}</option>
          ))}
        </select>
      </div>

      {coverage && (
        <>
          <div className={styles.summary}>
            <CoverageBar
              covered={coverage.covered}
              inUnitsOnly={coverage.inUnitsOnly}
              total={coverage.totalStandards}
            />
            <div className={styles.stats}>
              <button
                className={`${styles.statBtn} ${filter === 'all' ? styles.statBtnActive : ''}`}
                onClick={() => setFilter('all')}
              >
                <span className={styles.statNumber}>{coverage.totalStandards}</span>
                <span className={styles.statLabel}>Total</span>
              </button>
              <button
                className={`${styles.statBtn} ${styles.statCovered} ${filter === 'covered' ? styles.statBtnActive : ''}`}
                onClick={() => setFilter('covered')}
              >
                <span className={styles.statNumber}>{coverage.covered}</span>
                <span className={styles.statLabel}>In Lessons</span>
              </button>
              <button
                className={`${styles.statBtn} ${styles.statUnits} ${filter === 'inUnitsOnly' ? styles.statBtnActive : ''}`}
                onClick={() => setFilter('inUnitsOnly')}
              >
                <span className={styles.statNumber}>{coverage.inUnitsOnly}</span>
                <span className={styles.statLabel}>In Units Only</span>
              </button>
              <button
                className={`${styles.statBtn} ${styles.statUncovered} ${filter === 'uncovered' ? styles.statBtnActive : ''}`}
                onClick={() => setFilter('uncovered')}
              >
                <span className={styles.statNumber}>{coverage.uncovered}</span>
                <span className={styles.statLabel}>Not Covered</span>
              </button>
            </div>
          </div>

          <div className={styles.standardsList}>
            {Object.entries(grouped).map(([domainCode, group]) => (
              <div key={domainCode} className={styles.domainGroup}>
                <h3 className={styles.domainTitle}>
                  {domainCode} — {group.domainName}
                  <span className={styles.domainCount}>{group.standards.length}</span>
                </h3>
                {group.standards.map((s) => (
                  <StandardRow key={s.id} standard={s} status={s.status} />
                ))}
              </div>
            ))}
            {filtered.length === 0 && (
              <div className={styles.emptyFilter}>No standards match this filter.</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export function StandardsCoverageContent({ classId }) {
  const [coverage, setCoverage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (!classId) return;
    (async () => {
      try {
        setLoading(true);
        const result = await fetchStandardsCoverage(classId);
        if (result.success) setCoverage(result.data);
      } catch (err) {
        console.error('Failed to load coverage:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [classId]);

  const groupByDomain = (standards) => {
    const groups = {};
    for (const s of standards) {
      if (!groups[s.domainCode]) {
        groups[s.domainCode] = { domainName: s.domainName, standards: [] };
      }
      groups[s.domainCode].standards.push(s);
    }
    return groups;
  };

  const getFilteredStandards = () => {
    if (!coverage) return [];
    const all = [];
    for (const s of coverage.standards.covered) all.push({ ...s, status: 'covered' });
    for (const s of coverage.standards.inUnitsOnly) all.push({ ...s, status: 'inUnitsOnly' });
    for (const s of coverage.standards.uncovered) all.push({ ...s, status: 'uncovered' });
    if (filter === 'covered') return all.filter(s => s.status === 'covered');
    if (filter === 'inUnitsOnly') return all.filter(s => s.status === 'inUnitsOnly');
    if (filter === 'uncovered') return all.filter(s => s.status === 'uncovered');
    return all;
  };

  if (loading && !coverage) {
    return <div className={styles.loading}>Loading coverage...</div>;
  }

  const filtered = getFilteredStandards();
  const grouped = groupByDomain(filtered);

  return (
    <>
      {coverage && (
        <>
          <div className={styles.summary}>
            <CoverageBar
              covered={coverage.covered}
              inUnitsOnly={coverage.inUnitsOnly}
              total={coverage.totalStandards}
            />
            <div className={styles.stats}>
              <button className={`${styles.statBtn} ${filter === 'all' ? styles.statBtnActive : ''}`} onClick={() => setFilter('all')}>
                <span className={styles.statNumber}>{coverage.totalStandards}</span>
                <span className={styles.statLabel}>Total</span>
              </button>
              <button className={`${styles.statBtn} ${styles.statCovered} ${filter === 'covered' ? styles.statBtnActive : ''}`} onClick={() => setFilter('covered')}>
                <span className={styles.statNumber}>{coverage.covered}</span>
                <span className={styles.statLabel}>In Lessons</span>
              </button>
              <button className={`${styles.statBtn} ${styles.statUnits} ${filter === 'inUnitsOnly' ? styles.statBtnActive : ''}`} onClick={() => setFilter('inUnitsOnly')}>
                <span className={styles.statNumber}>{coverage.inUnitsOnly}</span>
                <span className={styles.statLabel}>In Units Only</span>
              </button>
              <button className={`${styles.statBtn} ${styles.statUncovered} ${filter === 'uncovered' ? styles.statBtnActive : ''}`} onClick={() => setFilter('uncovered')}>
                <span className={styles.statNumber}>{coverage.uncovered}</span>
                <span className={styles.statLabel}>Not Covered</span>
              </button>
            </div>
          </div>
          <div className={styles.standardsList}>
            {Object.entries(grouped).map(([domainCode, group]) => (
              <div key={domainCode} className={styles.domainGroup}>
                <h3 className={styles.domainTitle}>
                  {domainCode} — {group.domainName}
                  <span className={styles.domainCount}>{group.standards.length}</span>
                </h3>
                {group.standards.map((s) => (
                  <StandardRow key={s.id} standard={s} status={s.status} />
                ))}
              </div>
            ))}
            {filtered.length === 0 && (
              <div className={styles.emptyFilter}>No standards match this filter.</div>
            )}
          </div>
        </>
      )}
    </>
  );
}

export default StandardsCoveragePage;
